import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import './OnboardingFlow.css';
import { API_URL } from "../../utils/auth";
import Auth from '../../utils/auth';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

const OnboardingFlow = ({ setOnboardingProgress, clientId: propClientId }) => {
    const { clientId: paramClientId } = useParams();
    const clientId = propClientId || paramClientId;
    const workspaceId = Auth.getWorkspaceId();

    const [onboardingId, setOnboardingId] = useState(null); 
    const [steps, setSteps] = useState([
        { id: 1, slug: 'contract_signed', label: 'Contract Signed', completed: false, field: 'contract_signed' },
        { id: 2, slug: 'welcome_call', label: 'Welcome Call', completed: false, field: 'welcome_call' },
        { id: 3, slug: 'onboarding_survey', label: 'Onboarding Survey', completed: false, field: 'onboarding_survey' },
        { id: 4, slug: 'onboarding_call', label: 'Onboarding Call', completed: false, field: 'onboarding_call' },
        { id: 5, slug: 'group_access', label: 'Group Access', completed: false, field: 'group_access' },
    ]);
    const [settingsState, setSettingsState] = useState({
        data: [],
        loaded: false
    });

    const fetchOnboardingData = async () => {
        try {
            const response = await axios.get(`${API_URL}/onboarding/onboarding`, {
                params: { clientId, workspaceId }
            });

            if (response.data) {
                setOnboardingId(response.data.id); 
                setSteps(prevSteps =>
                    prevSteps.map(step => ({
                        ...step,
                        completed: response.data[step.field] || false
                    }))
                );

                if (response.data.onboardingProgress != null) {
                    setOnboardingProgress(Math.round(Number(response.data.onboardingProgress)));
                }
            }
        } catch (err) {
            toast.error('Error fetching onboarding data: ' + err.message);
        }
    };


    useEffect(() => {
        if (!clientId || !workspaceId) {
            console.error('Client ID or Workspace ID is missing!');
            return;
        }

        fetchOnboardingData();
    }, [clientId, workspaceId]);

    useEffect(() => {
        if (!workspaceId) return;

        const fetchSettings = async () => {
            try {
                const response = await axios.get(`${API_URL}/admin/onboarding-settings`, {
                    params: { workspaceId }
                });

                setSettingsState({
                    data: response.data || [],
                    loaded: true
                });
            } catch (error) {
                toast.error('Error fetching onboarding settings: ' + error.message);
                setSettingsState({
                    data: [],
                    loaded: true
                });
            }
        };

        fetchSettings();
    }, [workspaceId]);

    const handleStepClick = async (id) => {
        if (!onboardingId || !clientId || !workspaceId) {
            console.error('Missing required IDs!');
            return;
        }

        try {
            const updatedSteps = steps.map(step => 
                step.id === id ? { ...step, completed: !step.completed } : step
            );

            const putData = {
                id: onboardingId,
                clientId: Number(clientId),
                workspaceId: Number(workspaceId),
                contractSigned: updatedSteps.find(s => s.field === 'contract_signed').completed,
                welcomeCall: updatedSteps.find(s => s.field === 'welcome_call').completed,
                onboardingSurvey: updatedSteps.find(s => s.field === 'onboarding_survey').completed,
                onboardingCall: updatedSteps.find(s => s.field === 'onboarding_call').completed,
                groupAccess: updatedSteps.find(s => s.field === 'group_access').completed
            };

            await axios.put(`${API_URL}/onboarding/onboarding`, putData, {
                headers: {
                    'Content-Type': 'application/json',
                }
            });

            setSteps(updatedSteps);

            fetchOnboardingData();

        } catch (error) {
            console.error('Error updating onboarding:', {
                error: error.response?.data || error.message,
                status: error.response?.status,
                config: error.config
            });
            toast.error(`Error updating step: ${error.response?.data?.message || error.message}`);
        }
    };

    const visibleSteps = useMemo(() => {
        if (!settingsState.loaded) return [];
        
        return steps.filter(step => {
            const setting = settingsState.data.find(s => s.slug === step.slug);
            return setting ? setting.enabled : true;  
        });
    }, [settingsState.loaded, settingsState.data, steps]);

    return (
        <div className="onboarding-flow">
            <div className="onboarding-header">
                <h2>Onboarding Flow</h2>
                {settingsState.loaded ? (
                    <span className="progress-indicator">
                        {visibleSteps.filter(step => step.completed).length} of {visibleSteps.length}
                    </span>
                ) : (
                    <span className="progress-indicator">Loading...</span>
                )}
            </div>
            <div className="onboarding-steps">
                {!settingsState.loaded ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                        Loading onboarding options...
                    </div>
                ) : visibleSteps.length === 0 ? (
                    <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                        No onboarding enabled
                    </div>
                ) : (
                    visibleSteps.map(step => (
                        <div
                            key={step.id}
                            className={`onboarding-step ${step.completed ? 'completed' : 'incomplete'}`}
                            onClick={() => handleStepClick(step.id)}
                        >
                            <div className="status-icon">
                                {step.completed ? (
                                    <img style={{ width: '60%' }} src="/green_check.png" alt="check" />
                                ) : (
                                    <img style={{ width: '66%' }} src="/x_icon.png" alt="check" />
                                )}
                            </div>
                            <span className="step-label">{step.label}</span>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default OnboardingFlow;
